<template>
  <v-timeline side="end" density="compact" align="start" truncate-line="both" class="ms-steps">
    <v-timeline-item v-for="(s, i) in steps" :key="'s' + i" dot-color="surface-variant" size="small">
      <template #icon>
        <span class="text-caption font-weight-bold">{{ i + 1 }}</span>
      </template>

      <v-card color="background" variant="elevated" elevation="4" rounded="md" class="pa-4 ms-steps__card">
        <div class="d-flex align-center ga-2 mb-3">
          <v-text-field
            :model-value="s.name"
            variant="plain"
            density="compact"
            hide-details
            class="text-h6 font-weight-bold ms-steps__name"
            @update:model-value="(v) => patchStep(i, { name: v })"
          />
          <Button icon="mdi-trash-can-outline" tooltip="Remove step" size="small" :click="() => removeStep(i)" />
        </div>
        <div class="d-flex align-center flex-wrap ga-2">
          <span class="text-body-2 text-medium-emphasis">Run</span>
          <!-- The Advanced editor's Kind dropdown as a chip: a step runs an assistant or a
               workflow. Hidden when there's nothing to switch between (no workflows yet and the
               step already runs an assistant). -->
          <ChipSelect
            v-if="s.kind === 'workflow' || workflows.length"
            :model-value="s.kind === 'workflow' ? 'workflow' : 'assistant'"
            :options="kindOptions"
            @update:model-value="(v) => setKind(i, v)"
          />
          <ChipSelect
            v-if="s.kind === 'workflow'"
            :model-value="s.workflowName || 'Workflow'"
            :options="workflowOptions(s.workflowName)"
            icon="mdi-layers-triple-outline"
            @update:model-value="(v) => setWorkflow(i, v)"
          />
          <ChipSelect
            v-else
            :model-value="s.agent"
            :options="agentOptions(s.agent)"
            icon="mdi-robot-outline"
            @update:model-value="(v) => patchStep(i, { agent: v })"
          />
          <span class="text-body-2 text-disabled">·</span>
          <ChipSelect :model-value="thenLabel(s)" :options="thenOptions" @update:model-value="(v) => setThen(i, v)" />
        </div>

        <!-- Workflow steps mirror the Advanced editor's per-step payload controls: the schema the
             incoming data is validated against (shared FactorySchemaNode tree) and the fixer
             assistant + repair attempts that rewrite non-conforming data at run time. -->
        <v-expansion-panels v-if="s.kind === 'workflow'" variant="accordion" elevation="0" class="mt-3">
          <v-expansion-panel bg-color="background">
            <v-expansion-panel-title class="text-body-2">What this workflow receives</v-expansion-panel-title>
            <v-expansion-panel-text>
              <template v-if="schemaEditable(s)">
                <div class="text-caption text-medium-emphasis mb-3">
                  The fields this workflow expects as its starting data. Whatever the previous step
                  (or the starting data, if this step runs first) hands over must match.
                </div>
                <FactorySchemaNode
                  v-for="(field, fi) in schemaFieldsFor(s, i)"
                  :key="field.id"
                  :field="field"
                  :depth="0"
                  @update="(f) => updateSchemaField(i, fi, f)"
                  @delete="removeSchemaField(i, fi)"
                />
                <v-btn size="small" variant="tonal" prepend-icon="mdi-plus" @click="addSchemaField(i)">Add Field</v-btn>
              </template>
              <div v-else class="text-caption text-medium-emphasis">
                This step's expected input is too advanced to edit here — manage it in Advanced.
              </div>
              <div class="d-flex align-center flex-wrap ga-2 mt-4">
                <span class="text-body-2 text-medium-emphasis">If the data doesn't match,</span>
                <ChipSelect
                  :model-value="s.fixerAgent || 'no one'"
                  :options="fixerOptions(s.fixerAgent)"
                  icon="mdi-auto-fix"
                  @update:model-value="(v) => setFixer(i, v)"
                />
                <span class="text-body-2 text-medium-emphasis">rewrites it, up to</span>
                <ChipSelect
                  :model-value="String(s.maxRepairAttempts ?? 0)"
                  :options="repairAttemptOptions"
                  @update:model-value="(v) => patchStep(i, { maxRepairAttempts: parseInt(v, 10) || 0 })"
                />
                <span class="text-body-2 text-medium-emphasis">times.</span>
              </div>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card>
    </v-timeline-item>

    <v-timeline-item dot-color="surface" size="x-small">
      <v-btn variant="text" size="small" color="primary" prepend-icon="mdi-plus" @click="addStep">
        Add a step
      </v-btn>
    </v-timeline-item>
  </v-timeline>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import ChipSelect from './ChipSelect.vue'
import Button from '@/components/sub/Button.vue'
import FactorySchemaNode from '@/components/factories/FactorySchemaNode.vue'
import {
  createSchemaFieldDraft,
  isVisualizableFactorySchema,
  schemaFieldsFromSchema,
  schemaFromFields,
  type SchemaFieldDraft
} from '@/utils/factorySchemaTree'
import { exampleWorkflowPayloadSchema } from '@/utils/automations'
import type { AutoStep, ChipOption } from '@/types/automations'

@Options({
  components: { ChipSelect, Button, FactorySchemaNode },
  props: {
    steps: { type: Array, default: () => [] },
    agents: { type: Array, default: () => [] },
    // The user's workflows a step can run instead of an assistant ({ id, name }); steps store the
    // id (workflowRef.workflowConfigId) and show the name.
    workflows: { type: Array, default: () => [] }
  },
  emits: ['update:steps']
})
export default class FactorySteps extends Vue {
  steps!: AutoStep[]
  agents!: string[]
  workflows!: Array<{ id: string; name: string }>

  // Transitions are static config — next / loop / stop. No conditional branching (matches engine).
  get thenOptions(): ChipOption[] {
    return [
      { value: 'then continue' },
      { value: 'then finish' },
      ...this.steps.map((_, j) => ({ value: `then repeat from step ${j + 1}` }))
    ]
  }

  thenLabel(s: AutoStep): string {
    if (s.then === 'stop') return 'then finish'
    if (s.then === 'loop') return `then repeat from step ${(s.loopTo ?? 0) + 1}`
    return 'then continue'
  }

  agentOptions(current: string): ChipOption[] {
    return this.agents.includes(current) ? this.agents : [current, ...this.agents]
  }

  // 'workflow' is only offered once the user has one — switching seeds the first workflow so
  // autosave never persists an empty workflowConfigId (same spirit as addStep's agents[0] default).
  get kindOptions(): ChipOption[] {
    return this.workflows.length ? ['assistant', 'workflow'] : ['assistant']
  }

  workflowOptions(current?: string): ChipOption[] {
    const names = this.workflows.map((w) => w.name)
    return current && !names.includes(current) ? [current, ...names] : names
  }

  setKind(i: number, v: string) {
    const s = this.steps[i]
    if (v === 'workflow') {
      if (s.kind === 'workflow') return
      const w = this.workflows[0]
      if (!w) return
      // Same defaults Advanced seeds on a kind switch: example schema, no fixer, no repair attempts.
      this.patchStep(i, {
        kind: 'workflow',
        workflowId: w.id,
        workflowName: w.name,
        agent: '',
        payloadSchema: exampleWorkflowPayloadSchema(),
        fixerAgent: '',
        maxRepairAttempts: 0
      })
    } else {
      if (s.kind !== 'workflow') return
      this.patchStep(i, {
        kind: 'agent',
        agent: this.agents[0] || 'assistant',
        workflowId: undefined,
        workflowName: undefined,
        payloadSchema: undefined,
        fixerAgent: undefined,
        maxRepairAttempts: undefined
      })
    }
  }

  // Chips select by name; duplicate workflow names resolve to the first match (rename in Advanced
  // to disambiguate). A stale current name (deleted workflow) resolves to nothing and is a no-op.
  setWorkflow(i: number, name: string) {
    const w = this.workflows.find((x) => x.name === name)
    if (!w) return
    this.patchStep(i, { workflowId: w.id, workflowName: w.name })
  }

  fixerOptions(current?: string): ChipOption[] {
    const options = ['no one', ...this.agents]
    return current && !options.includes(current) ? [current, ...options] : options
  }

  setFixer(i: number, v: string) {
    this.patchStep(i, { fixerAgent: v === 'no one' ? '' : v })
  }

  // Same 0–5 range as the Advanced Repair Attempts input.
  get repairAttemptOptions(): ChipOption[] {
    return ['0', '1', '2', '3', '4', '5']
  }

  // A schema hand-built in Advanced can use JSON-schema features the visual tree can't represent;
  // editing it here would silently drop them, so it stays Advanced-only (same guard Advanced uses
  // to force its raw-JSON view).
  schemaEditable(s: AutoStep): boolean {
    return !s.payloadSchema || isVisualizableFactorySchema(s.payloadSchema)
  }

  // Per-step visual drafts of payloadSchema, cached with the schema JSON they were derived from.
  // While edits round-trip through the parent unchanged the cache holds — keeping FactorySchemaNode
  // field ids stable so typing doesn't lose focus; an external schema change rebuilds the draft.
  schemaDrafts: Record<string, { json: string; fields: SchemaFieldDraft[] }> = {}

  draftKey(s: AutoStep, i: number): string {
    return s.stepId ?? `new-${i}`
  }

  schemaFieldsFor(s: AutoStep, i: number): SchemaFieldDraft[] {
    const key = this.draftKey(s, i)
    const json = JSON.stringify(s.payloadSchema ?? null)
    const cached = this.schemaDrafts[key]
    if (cached && cached.json === json) return cached.fields
    const fields = schemaFieldsFromSchema(s.payloadSchema ?? {})
    this.schemaDrafts[key] = { json, fields }
    return fields
  }

  commitSchemaFields(i: number, fields: SchemaFieldDraft[]) {
    const s = this.steps[i]
    const schema = schemaFromFields(fields)
    this.schemaDrafts[this.draftKey(s, i)] = { json: JSON.stringify(schema), fields }
    this.patchStep(i, { payloadSchema: schema })
  }

  updateSchemaField(i: number, fieldIndex: number, field: SchemaFieldDraft) {
    const fields = this.schemaFieldsFor(this.steps[i], i).map((f, j) => (j === fieldIndex ? field : f))
    this.commitSchemaFields(i, fields)
  }

  removeSchemaField(i: number, fieldIndex: number) {
    const fields = this.schemaFieldsFor(this.steps[i], i).filter((_, j) => j !== fieldIndex)
    this.commitSchemaFields(i, fields)
  }

  addSchemaField(i: number) {
    const fields = [...this.schemaFieldsFor(this.steps[i], i), createSchemaFieldDraft('string')]
    this.commitSchemaFields(i, fields)
  }

  patchStep(i: number, patch: Partial<AutoStep>) {
    this.$emit(
      'update:steps',
      this.steps.map((s, j) => (j === i ? { ...s, ...patch } : s))
    )
  }

  setThen(i: number, v: string) {
    if (v === 'then continue') this.patchStep(i, { then: 'next', loopTo: undefined })
    else if (v === 'then finish') this.patchStep(i, { then: 'stop', loopTo: undefined })
    else {
      const m = v.match(/step (\d+)/)
      this.patchStep(i, { then: 'loop', loopTo: m ? parseInt(m[1], 10) - 1 : 0 })
    }
  }

  removeStep(i: number) {
    this.$emit(
      'update:steps',
      this.steps.filter((_, j) => j !== i)
    )
  }

  addStep() {
    const agent = this.agents[0] || 'assistant'
    this.$emit('update:steps', [...this.steps, { name: `Step ${this.steps.length + 1}`, agent, kind: 'agent', then: 'next' }])
  }
}
</script>

<style scoped lang="scss">
// Overflow guards: the timeline column must shrink with its container, the step card fills it, and
// the name field yields space instead of forcing the row wider than the card.
.ms-steps {
  :deep(.v-timeline-item__body) {
    min-width: 0;
    width: 100%;
  }
}

.ms-steps__card {
  width: 100%;
  min-width: 0;
}

.ms-steps__name {
  flex: 1 1 auto;
  min-width: 0;
}
</style>
